import React, { useReducer } from "react";
import pluralize from "pluralize";
import {
  Text,
  Button,
  Stack,
  Box,
  Card,
  Tooltip,
  Position,
  Shelf,
} from "@chaser/components";
import cx from "classnames";
import { Check } from "lucide-react";
import { Popup as PopupModal } from "../../../../../components/ChaserAlert/index";
import { immerReducer } from "../../../../../util/index";
import style from "./PlanBox.module.scss";
import useSubscribe from "../../../../../hooks/useSubscribe";
import useOrganisationInfo from "../../../../../hooks/useOrganisationInfo";
import { BillingPeriods, roles } from "../../../../../util/constants";
import useOrganisationUtils from "../../../../../hooks/useOrganisationUtils";
import { SUBSCRIBE_TYPES } from "../SubscriptionOffers/Plans/index.jsx";
import emitter from "../../../../../util/emitter";
import useRemoteConfig from "../../../../../hooks/useRemoteConfig";
import RoleGate, {
  hasRole,
} from "../../../../../components/FeatureGate/RoleGate";
import useUserPermissions from "../../../../../hooks/useUserPermissions";
import useShortCode from "../../../../../hooks/useShortCode";
import useModal from "../../../../../hooks/useModal";
import DowngradeModal from "./DowngradeModal";

const alertingReducer = immerReducer({
  setSuccess(state, message) {
    state.type = "success";
    state.message = message;
    state.error = undefined;
    state.open = true;
  },
  setError(state, message) {
    state.type = "error";
    state.error = message;
    state.message = undefined;
    state.open = true;
  },
  closeModal(state) {
    state.open = false;
  },
});

const boldColors = {
  basic_v2: "Orange AA",
  standard_v2: "Blue",
  enterprise_v2: "Dark Neutral 1",
};

const ADDON_LIMIT_LABELS = {
  sms: "SMS/month",
  phoneCall: "auto-calls/month",
  letter: "letters/month",
  emailIntegrationConnections: "email integrations",
};

const ADDON_FEATURE_KEYS = {
  sms: ["sms", "freeSms"],
  phoneCall: ["phoneCall", "freeCalls"],
  letter: ["letter", "freeLetters"],
  emailIntegrationConnections: ["emailIntegrationConnections"],
};

const formatAllowanceValue = (value) => {
  if (!Number.isFinite(value) || value <= 0) return null;
  return value >= 9999 ? "Unlimited" : String(value);
};

const resolveFeatureLimit = (features, addonKey) => {
  const keys = ADDON_FEATURE_KEYS[addonKey] || [addonKey];

  for (const key of keys) {
    const rawValue = features?.[key];
    if (typeof rawValue === "boolean") continue;

    const numericValue = Number(rawValue);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      return numericValue;
    }
  }

  return null;
};

function useSubscribePrompt(
  subscribeType,
  dispatch,
  { name, amount, currencyCode, period },
  openDowngradeModal,
) {
  const salesMeetingLink = useRemoteConfig("sales_meeting_link");
  const subscribeToSelected = useSubscribe(dispatch, {
    planName: name,
    currencyCode,
    period,
  });

  if (!amount) {
    return () => {
      if (salesMeetingLink) {
        return window.open(salesMeetingLink, "_blank");
      }

      return emitter.emit("openSupportModal", {
        subject: `I am trying to subscribe to ${period} ${name} ${amount} ${currencyCode}`,
        disableSubject: true,
      });
    };
  }

  switch (subscribeType) {
    case SUBSCRIBE_TYPES.DOWNGRADE:
      return openDowngradeModal;
    default:
      return subscribeToSelected;
  }
}

const SubscribeButton = ({
  isCurrent,
  hasAPrice,
  promptLabel,
  prompt,
  maxRevenue,
  disabled,
  currencyCode,
  calculatedAnnualRevenue,
  planName,
  ctaClassName,
}) => {
  const salesMeetingLink = useRemoteConfig("sales_meeting_link");
  const { formatCurrency } = useOrganisationUtils();
  const exceedsRevenue = calculatedAnnualRevenue?.value > maxRevenue;

  function showExceedMaximumModal() {
    if (salesMeetingLink) {
      return window.open(salesMeetingLink, "_blank");
    }

    return emitter.emit("openSupportModal", {
      subject: `I am trying to subscribe to ${planName} my revenue is ${currencyCode}${calculatedAnnualRevenue.value}`,
      disableSubject: true,
    });
  }

  const handlePromptClick = (event) => {
    event.stopPropagation();
    prompt();
  };

  if (isCurrent) {
    return (
      <Button appearance="secondary" disabled>
        <Text color="Mid Neutral 1">Current plan</Text>
      </Button>
    );
  }

  if (exceedsRevenue) {
    return (
      <Button
        onClick={(event) => {
          event.stopPropagation();
          showExceedMaximumModal();
        }}
      >
        Contact us
      </Button>
    );
  }

  return (
    <Tooltip
      position={Position.BOTTOM}
      disable={!exceedsRevenue}
      content={`The maximum revenue for this plan is ${formatCurrency(maxRevenue, currencyCode)}`}
    >
      <Button
        disabled={exceedsRevenue || disabled}
        onClick={handlePromptClick}
        className={ctaClassName}
        style={{ width: "100%" }}
      >
        {hasAPrice ? promptLabel : "Contact us"}
      </Button>
    </Tooltip>
  );
};

function getPlanPromptLabel(type) {
  switch (type) {
    case SUBSCRIBE_TYPES.UPGRADE:
      return "Upgrade";
    case SUBSCRIBE_TYPES.DOWNGRADE:
      return "Downgrade";
    default:
      return "Subscribe";
  }
}

const PlanBox = ({
  name,
  title,
  currencyCode,
  amount,
  formattedAmount,
  features,
  isCurrent,
  refetchSubscriptionData,
  onSubscribeSuccess,
  appearance = "default",
  isRecommended,
  period = BillingPeriods.MONTHLY,
  subscribeType = SUBSCRIBE_TYPES.CURRENT,
}) => {
  const { formatCurrency } = useOrganisationUtils();
  const { BaseCurrency, calculatedAnnualRevenue } = useOrganisationInfo();
  const userPermissions = useUserPermissions();
  const shortCode = useShortCode();
  const showModal = useModal();
  const [state, dispatch] = useReducer(alertingReducer, {
    type: undefined,
    message: "",
    open: false,
    error: undefined,
  });

  const maxRevenue = features?.maximumRevenue?.prices?.find(
    (f) => f.currency === currencyCode,
  )?.amount;

  const prompt = useSubscribePrompt(
    subscribeType,
    dispatch,
    {
      name,
      amount,
      currencyCode,
      period,
    },
    () => showModal(<DowngradeModal />),
  );

  const countryTax = (tax) => {
    switch (tax) {
      case "USD":
        return "excl Tax";
      case "AUD":
        return "excl GST";
      case "NZD":
        return "excl GST";
      default:
        return "excl VAT";
    }
  };

  function refetch() {
    refetchSubscriptionData();
    if (onSubscribeSuccess) onSubscribeSuccess(name);
  }

  const Comp = isRecommended ? Box : Stack;
  const hasAPrice = formattedAmount;
  const exceedsRevenue = calculatedAnnualRevenue?.value > maxRevenue;

  const includedFeatureRows = [];
  if (!exceedsRevenue && features.maximumRevenue) {
    includedFeatureRows.push(
      `Up to ${formatCurrency(maxRevenue, currencyCode)} revenue`,
    );
  }
  if (features.invoices) {
    includedFeatureRows.push(
      `${features.invoices === 9999 ? "Unlimited invoices" : `${features.invoices} invoices`} chased per month`,
    );
  }
  includedFeatureRows.push(
    `${features.templates === 9999 ? "Unlimited" : features.templates} templates`,
  );
  includedFeatureRows.push(
    `${features.schedules === 9999 ? "Unlimited" : features.schedules} ${pluralize("schedule", features.schedules)}`,
  );

  Object.keys(ADDON_LIMIT_LABELS).forEach((addonKey) => {
    const limit = resolveFeatureLimit(features, addonKey);
    const displayValue = formatAllowanceValue(limit);
    if (!displayValue) return;

    if (addonKey === "emailIntegrationConnections") {
      includedFeatureRows.push(
        `${displayValue} ${pluralize("email integration", limit)}`,
      );
      return;
    }

    includedFeatureRows.push(`${displayValue} ${ADDON_LIMIT_LABELS[addonKey]}`);
  });

  const userHasRole = hasRole(
    userPermissions,
    [roles.billing, roles.owner],
    shortCode,
  );

  const handleCardClick = () => {
    if (isCurrent || exceedsRevenue || !hasAPrice || !userHasRole) return;
    prompt();
  };

  const isClickable = !isCurrent && !exceedsRevenue && hasAPrice && userHasRole;
  const isDowngrade = subscribeType === SUBSCRIBE_TYPES.DOWNGRADE;

  return (
    <Card
      p={0}
      appearance={appearance}
      borderColor={isRecommended && "Light Blue 1"}
      borderWidth={isRecommended && "4px"}
      className={cx(
        style.card,
        isCurrent && style.currentCard,
        isDowngrade && !isCurrent && style.downgradeCard,
        isRecommended && style.animate,
        isClickable ? style.clickableCard : style.nonClickableCard,
      )}
      style={{
        opacity: exceedsRevenue ? 0.5 : 1,
        ...(isCurrent
          ? {
              background: "#fff8f4",
              border: "2px solid #ee8a67",
            }
          : {}),
      }}
      onClick={handleCardClick}
    >
      {isRecommended && (
        <Box
          bg="Light Blue 1"
          borderRadius="8px 8px 0px 0px"
          px={24}
          height="100%"
          py="xsmall"
          className={style.recommended}
          alignItems="center"
          justifyContent="center"
          display="flex"
        >
          <Text lineHeight={1.5} fontWeight="bold" color="White">
            Recommended
          </Text>
        </Box>
      )}
      <Comp gap="medium" height="100%" dividers p={!isRecommended && "medium"}>
        <Box
          p={!isRecommended ? "0 " : "medium"}
          height="100%"
          backgroundColor={isRecommended && "Light Blue 3"}
        >
          <Stack height="100%" gap="xsmall" justifyContent="space-between">
            <Stack dividers>
              <Stack gap="medium" height="90px">
                <Shelf alignItems="center" justifyContent="space-between">
                  <Text
                    color={boldColors[name]}
                    fontWeight="bold"
                    fontSize="heading.2"
                  >
                    {title}
                  </Text>
                  {isCurrent && (
                    <span className={style.currentBadge}>CURRENT</span>
                  )}
                </Shelf>
                <Stack gap="xsmall">
                  <Text fontSize="large" fontWeight="bold">
                    {formattedAmount || "POA"}
                  </Text>
                  <Text color="Mid Neutral 1">
                    per {period} ({countryTax(BaseCurrency)})
                  </Text>
                </Stack>
              </Stack>
              <Stack gap="xsmall" py="medium" justifyContent="center">
                {exceedsRevenue && features.maximumRevenue && (
                  <Text color="Dark Neutral 1">
                    {`Maximum revenue exceeded (${formatCurrency(maxRevenue, currencyCode)})`}
                  </Text>
                )}
                {includedFeatureRows.map((featureLabel) => (
                  <Box key={featureLabel} className={style.includedItem}>
                    <Check size={14} color="#E87750" />
                    <Text>{featureLabel}</Text>
                  </Box>
                ))}
              </Stack>
            </Stack>
            {!exceedsRevenue && (
              <RoleGate
                roles={[roles.billing, roles.owner]}
                renderGate={(_, children) => {
                  return (
                    <Tooltip
                      position={Position.BOTTOM}
                      content="This action needs additional permissions. Please contact your organisation owner to enable the plan & add-ons access you need."
                    >
                      {React.cloneElement(children, { disabled: true })}
                    </Tooltip>
                  );
                }}
              >
                <SubscribeButton
                  isCurrent={isCurrent}
                  hasAPrice={hasAPrice}
                  promptLabel={getPlanPromptLabel(subscribeType)}
                  prompt={prompt}
                  maxRevenue={maxRevenue}
                  currencyCode={currencyCode}
                  planName={name}
                  calculatedAnnualRevenue={calculatedAnnualRevenue}
                  ctaClassName={style.planCta}
                />
              </RoleGate>
            )}
          </Stack>
        </Box>
      </Comp>
      <PopupModal
        open={state.open}
        onClosed={() => refetch()}
        toggle={() => dispatch({ type: "closeModal" })}
        message={state.message}
        type={state.type}
        title=""
        error={state.error}
      />
    </Card>
  );
};

export default PlanBox;
